import { useParams, Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { siteConfig } from "@/mocks/site";
import { categoryData } from "@/mocks/category";
import ScrollToTop from "@/components/base/ScrollToTop";
import PageMeta from "@/components/base/PageMeta";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";

const categorySlugs = ["movie", "tv", "variety", "anime", "documentary"] as const;
type CategorySlug = (typeof categorySlugs)[number];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug || !categorySlugs.includes(slug as CategorySlug)) {
    return <Navigate to="/" replace />;
  }

  const cat = categoryData[slug as CategorySlug];

  const pageTitle = `${cat.label} - 在线高清观看 | 蜜桃影视`;
  const pageDesc = `${cat.description}。蜜桃影视为您提供最新${cat.label}在线观看，海量高清资源每日更新。`;
  const pageKeywords = `蜜桃影视${cat.label},${cat.label}在线观看,${cat.label}推荐,热门${cat.label},最新${cat.label}`;

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `蜜桃影视 - ${cat.label}`,
    description: cat.description,
    url: `${siteConfig.url}/category/${slug}`,
    isPartOf: {
      "@type": "WebSite",
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <PageMeta
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`${siteConfig.url}/category/${slug}`}
        schemaObjects={[collectionSchema]}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: cat.label },
            ]}
          />

          <div className="w-full px-4 md:px-6 lg:px-8 pb-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                  {cat.label}
                </h1>
                <span className="text-sm text-foreground-500">({cat.items.length}部作品)</span>
              </div>
              <p className="text-sm text-foreground-500 mb-4">{cat.description}</p>

              <div className="flex items-center gap-2 flex-wrap mb-6">
                {categorySlugs.map((s) => (
                  <Link
                    key={s}
                    to={`/category/${s}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      s === slug
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-50 text-foreground-600 hover:text-foreground-950 border border-background-200/70 hover:border-primary-200"
                    }`}
                  >
                    {categoryData[s].label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12" data-product-shop="">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {cat.items.map((item) => (
                <MovieCard
                  key={item.id}
                  {...item}
                  category={slug}
                  categoryLabel={cat.label}
                />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}